/**
 * Research in chat (docs/design/chat/DEEP_RESEARCH_V2_DESIGN.md, MHS-798). The model proposes a run; the reply carries the
 * proposal (`messageMetadata.tool_confirmation`, live as a `tool_confirmation` event) and the chat shows it as a card.
 * Once the owner starts it, the run streams `research_progress` events and then its cited report, saved with the
 * run's summary (`messageMetadata.research`). A run that fails is saved as an error reply with a RESEARCH_* code.
 */

import type { ResearchProgressData, SrcSchemasChatMessageResponse, ToolConfirmationData } from "@/api";

export const RESEARCH_TOOL = "research";

type Metadata = SrcSchemasChatMessageResponse["messageMetadata"];

const PROPOSAL_STATUSES = ["pending", "confirmed", "declined", "superseded"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const DEPTHS = ["standard", "deep"] as const;
export type ResearchDepth = (typeof DEPTHS)[number];
const TIME_RANGES = ["day", "week", "month", "year"] as const;
export type TimeRange = (typeof TIME_RANGES)[number];
/** Internal sources a run will read, as the backend resolved them when it proposed it (in this order). */
export const INTERNAL_SOURCES = ["files", "knowledge", "sharepoint", "drive"] as const;
export type InternalSource = (typeof INTERNAL_SOURCES)[number];

/** A proposed research run, as its card shows it. */
export interface ResearchProposal {
  /** The confirmation id a decision names. */
  id: string;
  status: ProposalStatus;
  brief: string;
  topics: string[];
  assumptions: string[];
  depth: ResearchDepth;
  scope: { web: boolean; internal: boolean; domains: string[]; timeRange: TimeRange | null };
  /** The internal sources that apply in this chat; null from a proposal made before the backend named them. */
  internalSources: InternalSource[] | null;
}

export type ResearchStage = NonNullable<ResearchProgressData["stage"]>;
export type TopicStatus = "pending" | "active" | "done" | "skipped";
export interface ResearchTopic {
  id: string;
  title: string;
  status: TopicStatus;
}
export interface ResearchCounts {
  queries: number;
  sources: number;
  pages: number;
}

/** A page a running research read: a web page (with its link) or an internal document. */
export interface ResearchPage {
  title: string;
  url: string | null;
}

/** A research run while it streams, at its latest `research_progress`. */
export interface ResearchRunView {
  toolCallId: string;
  stage: ResearchStage;
  round: number;
  topics: ResearchTopic[];
  counts: ResearchCounts;
  /** The newest query run or page read, or while the report is written, the writer's current step. */
  latest: { kind: "query" | "source" | "thought"; text: string } | null;
  /** The searches run so far, oldest first (the newest 40). */
  searches: string[];
  /** The pages read so far, oldest first (the newest 40). */
  pages: ResearchPage[];
}

/** A finished run, from the saved report's `messageMetadata.research`. */
export interface ResearchSummaryView {
  depth: ResearchDepth;
  rounds: number;
  counts: ResearchCounts;
  durationMs: number;
  /** Cut short (time limit, monthly budget, web search down): written from what was found by then. */
  partial: boolean;
  partialReason: string | null;
  topics: ResearchTopic[];
}

/** Why a run ended without a report; the chat explains each in its own words. */
export const RESEARCH_ERROR_CODES = [
  "RESEARCH_IN_PROGRESS",
  "RESEARCH_UNAVAILABLE",
  "RESEARCH_FAILED",
  "RESEARCH_NO_SOURCES",
] as const;
export type ResearchErrorCode = (typeof RESEARCH_ERROR_CODES)[number];

/** Partial reasons with their own notice; any other reason gets the general one. */
const PARTIAL_REASONS = ["time", "budget", "web"] as const;
export type PartialNotice = (typeof PARTIAL_REASONS)[number] | "other";

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const oneOf = <T extends string>(options: readonly T[], value: unknown): value is T =>
  (options as readonly unknown[]).includes(value);
const count = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : 0);
/** Non-empty strings, each once. */
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? [...new Set(value.filter((v): v is string => typeof v === "string" && v.trim() !== ""))] : [];

function internalSourcesOf(details: unknown): InternalSource[] | null {
  const listed = record(details).internal_sources;
  if (!Array.isArray(listed)) return null;
  return INTERNAL_SOURCES.filter((source) => listed.includes(source));
}

function proposalOf(
  id: unknown,
  tool: unknown,
  rawArguments: unknown,
  status: unknown,
  details: unknown,
): ResearchProposal | null {
  if (tool !== RESEARCH_TOOL || typeof id !== "string" || !id) return null;
  const args = record(rawArguments);
  const scope = record(args.scope);
  return {
    id,
    // A status this client doesn't know can't be acted on.
    status: oneOf(PROPOSAL_STATUSES, status) ? status : "superseded",
    brief: typeof args.brief === "string" ? args.brief.trim() : "",
    topics: strings(args.topics),
    assumptions: strings(args.assumptions),
    depth: args.depth === "deep" ? "deep" : "standard",
    scope: {
      web: scope.web !== false,
      internal: scope.internal !== false,
      domains: strings(scope.domains),
      timeRange: oneOf(TIME_RANGES, scope.time_range) ? scope.time_range : null,
    },
    internalSources: internalSourcesOf(details),
  };
}

/** The research proposal a saved reply carries, or null. */
export function proposalFromMetadata(metadata: Metadata): ResearchProposal | null {
  const saved = record(metadata?.tool_confirmation);
  return proposalOf(saved.id, saved.tool, saved.arguments, saved.status, saved.details);
}

/** The research proposal a reply just made (pending until decided), or null for another tool. */
export function proposalFromEvent(data: ToolConfirmationData): ResearchProposal | null {
  return proposalOf(data.confirmation_id, data.tool, data.arguments, "pending", record(data).details);
}

function topicsOf(value: unknown): ResearchTopic[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const topic = record(item);
    if (typeof topic.id !== "string" || typeof topic.title !== "string") return [];
    const status = oneOf(["pending", "active", "done", "skipped"] as const, topic.status) ? topic.status : "pending";
    return [{ id: topic.id, title: topic.title, status }];
  });
}

function pagesOf(value: unknown): ResearchPage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const page = record(item);
    const url = typeof page.url === "string" && page.url ? page.url : null;
    const title = typeof page.title === "string" ? page.title.trim() : "";
    return title || url ? [{ title, url }] : [];
  });
}

const countsOf = (value: unknown): ResearchCounts => {
  const counts = record(value);
  return { queries: count(counts.queries), sources: count(counts.sources), pages: count(counts.pages) };
};

/**
 * Each `research_progress` carries the run's whole state, so the newest replaces what came before: a replay from the
 * start and a resume part-way through both end on the same view.
 */
export function withResearchProgress(
  runs: Readonly<Record<string, ResearchRunView>>,
  data: ResearchProgressData,
): Record<string, ResearchRunView> {
  const latest = data.latest?.text ? { kind: data.latest.kind ?? "query", text: data.latest.text } : null;
  const before = runs[data.tool_call_id];
  return {
    ...runs,
    [data.tool_call_id]: {
      toolCallId: data.tool_call_id,
      stage: data.stage ?? "planning",
      round: data.round ?? 1,
      topics: topicsOf(data.topics),
      counts: countsOf(data.counts),
      latest: latest ?? before?.latest ?? null,
      // From a backend that doesn't send them yet, keep what came before
      searches: data.searches ? strings(data.searches) : (before?.searches ?? []),
      pages: data.pages ? pagesOf(data.pages) : (before?.pages ?? []),
    },
  };
}

/** The finished run behind a saved report, or null (not a research report, or one imported from v1). */
export function researchFromMetadata(metadata: Metadata): ResearchSummaryView | null {
  const raw = metadata?.research;
  if (!raw || typeof raw !== "object") return null;
  const research = record(raw);
  if (research.legacy === true) return null;
  return {
    depth: research.depth === "deep" ? "deep" : "standard",
    rounds: count(research.rounds),
    counts: countsOf(research.counts),
    durationMs: count(research.duration_ms),
    partial: research.partial === true,
    partialReason: typeof research.partial_reason === "string" ? research.partial_reason : null,
    topics: topicsOf(research.topics),
  };
}

export const partialNotice = (reason: string | null): PartialNotice =>
  oneOf(PARTIAL_REASONS, reason) ? reason : "other";

export const isResearchErrorCode = (code: unknown): code is ResearchErrorCode => oneOf(RESEARCH_ERROR_CODES, code);

/** The code of a saved reply for a research run that failed (an error reply with a RESEARCH_* code), or null. */
export function researchFailure(metadata: Metadata): ResearchErrorCode | null {
  return metadata?.status === "error" && isResearchErrorCode(metadata.code) ? metadata.code : null;
}
